import { Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton, InputAdornment, Skeleton, Stack, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tabs, TextField, Typography, useTheme } from "@mui/material";
import { IconCheck, IconChevronDown, IconChevronUp, IconClock, IconCopy, IconEdit, IconFile, IconFileDollar, IconFileText, IconMail, IconMessage, IconMoodSmile, IconMoodSmileFilled, IconPlus, IconSearch, IconSend, IconUpload } from "@tabler/icons-react";
import { getRandomNumber, idGenerator, handleUpload, getDocumentType, getFileType, convertDate, getStringHash } from "../util/helpers";
import { useEffect, useState, useContext, useRef, useMemo, useCallback } from "react";
import { useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { fetchGet, fetchPost, fetchPatch, transformFirebaseTime, fetchPut } from "../util/helpers";
import UserContext from "../contexts/UserContext";
import { FileViewer } from "../components";
import LoadingContext from "../contexts/LoadingContext";
import { getDownloadURL, getStorage, ref } from "firebase/storage";
import { DocumentEditorContainerComponent } from "@syncfusion/ej2-react-documenteditor";

export default function WorkstreamTab({ matches, campaign }) {
    const theme = useTheme();
    const [selectedCreator, setSelectedCreator] = useState(null);
    const [tabValue, setTabValue] = useState("chat");
    const [reviewContentOpen, setReviewContentOpen] = useState(false);
    const { campaignId } = useParams();
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState("");
    const { user } = useContext(UserContext);
    const [briefs, setBriefs] = useState([]);
    const fileRef = useRef(null);
    const chatEndRef = useRef(null);
    const [concept, setConcept] = useState("");
    const [deliverable, setDeliverable] = useState({});
    const [allDeliverables, setAllDeliverables] = useState([]);
    const [approveContent, setApproveContent] = useState({ approved: false });
    const [approveConcept, setApproveConcept] = useState({ approved: false });
    const [feedback, setFeedbak] = useState("");
    const [feedbackList, setFeedbackList] = useState([]);
    const [briefLoaded, setBriefLoaded] = useState(false);
    const [briefModalOpen, setBriefModalOpen] = useState(false);
    const [briefURL, setBriefURL] = useState("");
    const iframeRef = useRef(null);
    const briefInterval = useRef();
    const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);
    const editorMutableRef = useRef();
    const { setIsLoading, setLoadingText } = useContext(LoadingContext);
    const [trackingModalOpen, setTrackingModalOpen] = useState(false);
    const [productURL, setProductURL] = useState("");
    const [products, setProducts] = useState([]);
    const [liveDeliverables, setLiveDeliverables] = useState([]);
    const [editingCosts, setEditingCosts] = useState({});
    const handleCloseProductModal = () => {
        setTrackingModalOpen(false);
        setProductURL("");
    };

    const fetchProducts = async () => {
        if (campaignId && selectedCreator) {
            const results = await fetchGet(
                `/product/redirect?campaignId=${campaignId}&creatorUserDocId=${selectedCreator.userDocId}`,
            );
            setProducts(results.result);
        }
    };
    const handleGenerateURL = async () => {
        await fetchPost(`/product/redirect`, {
            productURL,
            campaignId,
            creatorUserDocId: selectedCreator.userDocId,
        });
        await handleCloseProductModal();
        await fetchProducts();
    };


    const INTERNAL_PLATFORMS = ["draft", "concept", "creator-brief", "Approve-Content", "Approve-Concept", "Creator-Feedback"];

    const fetchLiveDeliverables = async () => {
        if (campaignId && selectedCreator) {
            const results = await fetchGet(
                `/brand/deliverables?campaignId=${campaignId}&deliveredBy=${selectedCreator.userDocId}`,
            );
            if (results.result) {
                const live = results.result.filter(d => !INTERNAL_PLATFORMS.includes(d.platform));
                setLiveDeliverables(live);
                const initial = {};
                live.forEach(d => {
                    initial[d.id] = {
                        totalPrice: d.totalPrice ?? "",
                        affiliateLink: d.affiliateLink ?? "",
                    };
                });
                setEditingCosts(initial);
            }
        }
    };

    const handleSaveCost = async (deliverableId) => {
        const edits = editingCosts[deliverableId];
        if (!edits) return;
        try {
            await fetchPatch(`/brand/deliverables/${deliverableId}`, {
                totalPrice: parseFloat(edits.totalPrice) || 0,
                affiliateLink: edits.affiliateLink || "",
            });
            toast.success("Saved");
            await fetchLiveDeliverables();
        } catch (error) {
            console.error("Failed to save cost:", error);
            toast.error("Failed to save. Please try again.");
        }
    };

    const slotProps = {
        formHelperText: { fontSize: "9px" },
        input: { fontSize: "9px" },
        inputLabel: { fontSize: "9px" }
    }

    const tabs = [
        { name: "Chat", value: "chat" },
        { name: "Documents", value: "documents" },
        { name: "Review Content", value: "review-content" },
        { name: "Affiliate", value: "affiliate" },
        { name: "Post Costs", value: "post-tracking" },
    ]

    const CONTENT_STATUS_TO_LABEL = {
        approved: "Approved",
        pending: "Pending",
    }

    const CONTENT_STATUS_TO_COLOR = {
        pending: { backgroundColor: theme.palette.info.light, color: theme.palette.info.main },
        approved: { backgroundColor: theme.palette.success.light, color: theme.palette.info.main }
    };

    const handleFetchMessages = async () => {
        const messages = await fetchGet(
            `/chat/campaigns/${campaignId}?creatorUserDocId=${selectedCreator.userDocId}`,
        );
        setMessages(messages.result);
    };

    useEffect(() => {
        if (campaignId && selectedCreator) {
            handleFetchMessages();
            getCompanyUploadedDeliverables();
            getAllDeliverables();
            fetchProducts();
            fetchLiveDeliverables();
            
            // Poll for new messages every 5 seconds
            const messageInterval = setInterval(() => {
                handleFetchMessages();
            }, 5000);
            
            return () => clearInterval(messageInterval);
        }
    }, [campaignId, selectedCreator]);

    // Scroll to bottom when messages change
    useEffect(() => {
        if (chatEndRef.current) {
            chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages]);

    const sendMessage = async () => {
        if (newMessage.trim().length === 0 || !selectedCreator || !campaignId) {
            return;
        }
        await fetchPost("/chat/campaigns", {
            message: newMessage,
            sender: user.userDocId,
            campaignId: campaignId,
            recipient: selectedCreator.userDocId,
            sendingTime: new Date()
        });
        setNewMessage("");

        await handleFetchMessages();
    };

    const getCompanyUploadedDeliverables = async () => {
        const results = await fetchGet(
            `/brand/deliverables?campaignId=${campaignId}&deliveredBy=${user.userDocId}`,
        );
        if (results.result) {
            const tempFeedbackList = [];
            const tempBriefs = [];
            results.result.forEach((item) => {
                // if (item.platform === "creator-agreement") {
                //   setAgreement(item.link);
                // } else 
                if (item.platform === "creator-brief") {
                    // Only show briefs for the selected creator or briefs without a creatorId (shared with all)
                    if (!item.creatorId || item.creatorId === selectedCreator.userDocId) {
                        tempBriefs.push(item);
                    }
                }
            });
            const creatorReviews = results.result.filter((data) => data.creatorId !== undefined && data.creatorId === selectedCreator.userDocId);
            if (creatorReviews.length > 0) {
                creatorReviews.forEach((item) => {
                    if (item.platform === "Approve-Content") setApproveContent({ ...item });
                    else if (item.platform === "Approve-Concept") setApproveConcept({ ...item });
                    else if (item.platform === "Creator-Feedback") {
                        tempFeedbackList.push({ ...item, submittedAt: new Date(item.submittedAt._seconds * 1000 + item.submittedAt._nanoseconds / 1000000) });
                    }
                })
            } else {
                setApproveContent({ approved: false });
                setApproveConcept({ approved: false });
            }
            tempFeedbackList.sort((a, b) => b.submittedAt - a.submittedAt)
            setFeedbackList([...tempFeedbackList]);
            setBriefs([...tempBriefs]);
        }
    };

    const getAllDeliverables = async () => {
        const results = await fetchGet(
            `/brand/deliverables?campaignId=${campaignId}&deliveredBy=${selectedCreator.userDocId}`,
        );

        if (results.result) {
            const resultDeliverables = [];
            results.result.forEach((item) => {
                if (item.platform === "concept") {
                    setConcept(item.link);
                } else if (item.platform === "draft") {
                    resultDeliverables.push({ ...item });
                }
            });
            resultDeliverables.sort((a, b) => transformFirebaseTime(b.submittedAt) - transformFirebaseTime(a.submittedAt))
            setDeliverable(resultDeliverables[0]);
            setAllDeliverables(resultDeliverables);

        }
    };

    const handleAddFile = async (url, type, size, fileType, fileName) => {
        return fetchPost(`/brand/deliverables`, {
            campaignId: campaignId,
            platform: type,
            link: url,
            size: size,
            fileType: getDocumentType(fileName),
            fileName: fileName.split(".")[0],
            creatorId: selectedCreator.userDocId,
            deliveredBy: user.userDocId
        });
    };

    useEffect(() => {
        if (matches) {
            console.log("matches", matches)
            let filtered = matches.filter((creator) => creator.name)
            if (filtered.length > 0) {

                setSelectedCreator(filtered[0])
            }
        }
    }, [matches])
    const userColor = useMemo(() => {
        return `../gradient${getStringHash(user?.name, 10)}.png`
    }, [])
    console.log("messages", messages)

    const submitFeedback = async () => {
        const result = await fetchPost(`/brand/deliverables`, {
            campaignId: campaignId,
            platform: "Creator-Feedback",
            feebback: feedback,
            creatorId: selectedCreator.userDocId
        });
        getCompanyUploadedDeliverables();
        setFeedbak("");
    }

    const approveConceptContent = async (type, status) => {

        const id = (type === "Approve-Concept") ? approveConcept.id : approveContent.id;
        const reqBody = {
            campaignId: campaignId,
            platform: type,
            approved: status,
            creatorId: selectedCreator.userDocId
        }
        const result = id ? await fetchPut(`/brand/deliverables/${id}`, { ...reqBody }) : await fetchPost(`/brand/deliverables`, { ...reqBody })
        if (type === "Approve-Content") setApproveContent({ ...result.data }); else setApproveConcept({ ...result.data });
    }
    const generateBrief = async () => {
        setLoadingText("Generating brief");
        fetchPost(
            "/brand/brief/generate",
            {
                campaignId: campaignId,
                brandName: campaign.brand,
                campaignName: campaign.name,
                campaignDeliverables: campaign.deliverables,
                campaignDetails: campaign.description,
                campaignTalkingPoints: campaign.talking_points,
                campaignWrittenTimeline: campaign.written_due,
                campaignDraftTimeline: campaign.draft_due,
                campaignFinalTimeline: campaign.final_due,
            },
            setIsLoading,
        )
            .then(() => {
                const storage = getStorage();
                getDownloadURL(ref(storage, `briefs/${campaignId}.docx`)).then(
                    (url) => {
                        setBriefURL(url);
                        setBriefModalOpen(true);
                    },
                );
            })
            .catch((err) => {
                console.log("error", err);
                alert("An error occured generating the brief");
            });
    };
    const clearCheckingInterval = () => {
        clearInterval(briefInterval.current);
    };
    const onIframeLoaded = () => {
        clearCheckingInterval();
        setBriefLoaded(true);
    };
    const handleDownloadInvoice = () => {
        if (editorMutableRef.current) {
            editorMutableRef.current.documentEditor.save("invoice", "Docx");
        }
    };
    const onLoadDefault = (ref) => {
        // tslint:disable
        let defaultDocument = {
            sfdt: "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",
        };
        // tslint:enable
        ref.documentEditor.open(JSON.stringify(defaultDocument));
        ref.documentEditor.documentName = "Invoice";
        ref.documentChange = () => {
            ref.documentEditor.focusIn();
        };
    };
    const editorRef = useCallback(
        (ref) => {
            const rendereComplete = () => {
                editorMutableRef.current = ref;
                ref.documentEditor.pageOutline = "#E0E0E0";
                ref.documentEditor.acceptTab = true;
                ref.documentEditor.resize();
                onLoadDefault(ref);
            };
            if (invoiceModalOpen && ref) {
                rendereComplete();
            }
        },
        [invoiceModalOpen],
    );
    if (!matches || matches.length === 0) return <Box style={{ width: "100%", height: "90vh", display: "flex", justifyContent: "center", alignItems: "center" }}><Typography variant="h4" color="var(--main-text-color)">No matches found</Typography></Box>
    return (
        <Stack direction="column" spacing={2} sx={{ padding: "3%" }}>
            <Dialog
                open={invoiceModalOpen}
                fullWidth
                onClose={() => setInvoiceModalOpen(false)}
                maxWidth="lg"
            >
                <DialogTitle>Generate an invoice with our template</DialogTitle>
                <DialogContent sx={{ height: "80vh" }}>
                    <DocumentEditorContainerComponent
                        ref={editorRef}
                        id={`doc-editor`} height={`${window.innerHeight * 0.7}px`} style={{ 'width': '100%' }} serviceUrl="https://ej2services.syncfusion.com/production/web-services/api/documenteditor/" enableToolbar={true}
                        enableWordExport
                        locale="en-US"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDownloadInvoice}>Download</Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={briefModalOpen}
                fullWidth
                onClose={() => setBriefModalOpen(false)}
                key={`dialog-${briefURL}`}
            >
                <DialogTitle>Generated Brief</DialogTitle>
                <DialogContent>
                    <Typography style={{ marginBottom: 20 }}>
                        <a href={briefURL} rel="noopener noreferrer" target="_blank">
                            Download Your Modifiable Brief
                        </a>
                    </Typography>
                    {!briefLoaded && (
                        <Skeleton
                            width="100%"
                            height={600}
                            variant="rectangular"
                            animation="wave"
                        />
                    )}
                    <iframe
                        ref={iframeRef}
                        title="file"
                        width="100%"
                        height="600"
                        onLoad={onIframeLoaded}
                        src={
                            "https://docs.google.com/viewer?url=" +
                            encodeURIComponent(briefURL) +
                            "&embedded=true"
                        }
                    ></iframe>
                </DialogContent>

            </Dialog>

            <Stack direction="row" sx={{ backgroundColor: "white", borderRadius: "var(--radius-lg)", minHeight: "600px" }}>
                <Stack direction="column" spacing={2} sx={{ padding: "15px" }} width="20%">
                    <TextField
                        placeholder="Search Partners"
                        size="small"
                        variant="outlined"
                        color="info"
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <IconSearch className="icon-colored" size="18" color={theme.palette.info.main} />
                                    </InputAdornment>
                                ),
                            },
                        }} />
                    <Stack direction="column" height="100%" sx={{ overflowY: "scroll" }}>
                        {matches && matches.filter((creator) => creator.name).map((creator) =>
                            <Box className="creator-list-item" sx={{ backgroundColor: selectedCreator?.id === creator?.id ? "var(--neutral-100)" : "white" }} onClick={() => setSelectedCreator(creator)}>
                                <Stack direction="row" spacing={2} alignItems="center">
                                    <img className="profile-image-medium" src={creator?.images?.length > 0 ? creator?.images[0] : `../gradient${getStringHash(creator?.name, 10)}.png`} alt={`${selectedCreator?.name || "Selected Creator"} profile`} />
                                    <Stack direction="column" spacing={0}>
                                        <Typography variant="bodyregm" color="var(--main-text-color)">{creator?.name}</Typography>
                                    </Stack>
                                </Stack>
                            </Box>
                        )}
                    </Stack>
                </Stack>
                <Divider orientation="vertical" flexItem />
                <Stack direction="column" sx={{ paddingTop: "20px" }} width="80%">
                    <Stack direction="column" spacing={2} sx={{ borderBottom: 1, borderColor: 'divider' }}>
                        <Stack direction="row" justifyContent="space-between" sx={{ paddingX: "20px" }}>
                            <Stack direction="row" spacing={2} alignItems="center">
                                <img className="profile-image" src={selectedCreator?.images?.length > 0 ? selectedCreator?.images[0] : `../gradient${getStringHash(selectedCreator?.name, 10)}.png`} alt={`${selectedCreator?.name || "Selected Creator"} profile`} />
                                <Stack direction="column" spacing={0}>
                                    <Typography variant="bodyregm" color="var(--main-text-color)">{selectedCreator?.name || "Selected Creator"}</Typography>
                                </Stack>
                            </Stack>
                            <Stack direction="row" spacing={2} alignItems="center">
                                <Button onClick={() => setInvoiceModalOpen(true)} variant="outlined" className="outlined-button" color="info" startIcon={<IconFileDollar className="icon-colored" size="18" color={theme.palette.info.main} />}>Generate Invoice</Button>

                                <Button onClick={generateBrief} variant="outlined" className="outlined-button" color="info" startIcon={<IconFileText className="icon-colored" size="18" color={theme.palette.info.main} />}>Generate Creator Brief</Button>
                                <Button href={`mailto:${selectedCreator?.email}`} variant="outlined" className="outlined-button" color="info" startIcon={<IconMail className="icon-colored" size="18" color={theme.palette.info.main} />}>Send Email</Button>
                                <Button 
                                    onClick={() => {
                                        navigator.clipboard.writeText(selectedCreator?.email || '');
                                        toast.success('Email copied to clipboard!');
                                    }} 
                                    variant="outlined" 
                                    className="outlined-button" 
                                    color="info" 
                                    startIcon={<IconCopy className="icon-colored" size="18" color={theme.palette.info.main} />}
                                >
                                    Copy Email
                                </Button>
                            </Stack>
                        </Stack>
                        <Tabs value={tabValue} onChange={(e, value) => setTabValue(value)} indicatorColor="secondary">
                            {tabs.map((tab) => (
                                <Tab className={tabValue === tab.value ? "gradient-text" : ""} label={tab.name} value={tab.value} />
                            ))}
                        </Tabs>
                    </Stack>
                    {tabValue === "chat" &&
                        <Stack direction="column" sx={{ justifyContent: "end", height: "100%" }}>
                            <Box sx={{ display: "block", flexDirection: "column", justifyContent: "end", height: "345px", overflowY: "auto" }}>
                                {
                                    messages.map((messageData, index) => {
                                        return <>
                                            <Stack key={index} direction="row" spacing={2} sx={{ padding: "10px", width: "100%", justifyContent: "end" }}>
                                                {messageData.sender !== user.userDocId && <img className="profile-image-small" src={selectedCreator?.images?.length > 0 ? selectedCreator?.images[0] : `../gradient${getStringHash(selectedCreator?.name, 10)}.png`} alt={`${selectedCreator?.name || "Selected Creator"} profile`} />}
                                                <Stack direction="column" spacing={0.5} width="100%" alignItems={`${messageData.sender == user.userDocId && "end"}`}>
                                                    <Stack direction="row" spacing={1} alignItems="center">
                                                        <Typography variant="bodyxs" color="var(--main-text-color)">{messageData.sender == user.userDocId ? "You" : selectedCreator?.name || "Selected Creator"}</Typography>
                                                        <Typography variant="bodyxs" color="var(--body-text-color-200)">{transformFirebaseTime(messageData.submittedAt).toLocaleTimeString('en-US', {
                                                            hour: '2-digit',
                                                            minute: '2-digit',
                                                            hour12: true
                                                        })}</Typography>
                                                    </Stack>
                                                    <Box className={`chat-bubble-${messageData.sender !== user.userDocId ? "partner" : "user"}`}>
                                                        <Typography variant="bodysm" color={`${messageData.sender != user.userDocId ? "var(--main-text-color)" : "white"}`}> {messageData.message}</Typography>
                                                    </Box>
                                                </Stack>
                                                {messageData.sender == user.userDocId && <img className="profile-image-small" src={userColor} alt={`${selectedCreator?.name || "Selected Creator"} profile`} />}
                                            </Stack>
                                        </>
                                    })
                                }
                                <div ref={chatEndRef} />
                            </Box>
                            {/* </Stack> */}
                            <Divider />
                            <Stack direction="row" sx={{ padding: "10px" }}>
                                <TextField
                                    id="message-input"
                                    placeholder="Type your message here..."
                                    multiline
                                    maxRows={4}
                                    variant="standard"
                                    fullWidth
                                    sx={{ overflowY: "scroll" }}
                                    color="secondary"
                                    value={newMessage}
                                    onChange={(e) => setNewMessage(e.target.value)}
                                />
                            </Stack>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ padding: "10px" }}>
                                <IconButton >
                                    <IconMoodSmileFilled className="icon-colored" size="20" color={theme.palette.info.main} />
                                </IconButton>
                                <Button onClick={() => sendMessage()} variant="contained" className="gradient-button" color="info" startIcon={<IconSend className="icon" size="18" />}>Send</Button>
                            </Stack>
                        </Stack>
                    }
                    {tabValue === "documents" && <Stack direction="column" spacing={2} padding="20px" height="100%">
                        <Typography variant="h4" color="var(--main-text-color)">Documents</Typography>
                        <Box sx={{ display: "block", flexDirection: "column", justifyContent: "end", height: "345px", overflowY: "auto" }}>
                            <Stack direction="column" sx={{ border: 1, borderColor: theme.palette.divider, borderRadius: "var(--radius-lg)" }}>
                                {/* Map documents here */}
                                {
                                    briefs.map((briefData, index) => <Stack key={index} direction="column">
                                        <Stack direction="row" spacing={2} sx={{ padding: "10px" }} alignItems="center" justifyContent="space-between">
                                            <Stack direction="row" spacing={2} alignItems="center">
                                                <IconFile className="icon-colored" size="30px" color="var(--body-text-color-500)" />
                                                <Stack direction="column">
                                                    <Typography variant="bodyreg" color="var(--main-text-color)">{briefData.fileName || `Document ${index}`}</Typography>
                                                    <Stack direction="row" spacing={1} alignItems="center">
                                                        <Typography variant="bodysm" color="var(--body-text-color-200)">{briefData.fileType || "Document"} Type</Typography>
                                                        <Box className="separator-dot" sx={{ backgroundColor: theme.palette.info.light }} />
                                                        <Typography variant="bodysm" color="var(--body-text-color-200)">{briefData.fileSize || ""}</Typography>
                                                    </Stack>
                                                </Stack>
                                            </Stack>
                                            {briefData.link && (
                                                <Button 
                                                    variant="outlined" 
                                                    size="small" 
                                                    onClick={() => window.open(briefData.link, '_blank')}
                                                >
                                                    View
                                                </Button>
                                            )}
                                        </Stack>
                                        <Divider />
                                    </Stack>)
                                }
                            </Stack>
                        </Box>
                        <Stack direction="row">
                            <input style={{ display: "none" }} type="file" ref={fileRef} onChange={async (e) => {
                                const files = e.target.files;
                                if (files !== null && files?.length > 0) {
                                    const file = files[0];
                                    const fileId = idGenerator();
                                    const fileExtension = file.name.split('.').pop();
                                    const url = await handleUpload(
                                        file,
                                        "creator-brief",
                                        `campaign-${campaignId}-${fileId}.${fileExtension}`,
                                    );
                                    await handleAddFile(url, "creator-brief", files[0].size, files[0].type, files[0].name);
                                    await getCompanyUploadedDeliverables();
                                }
                            }} />
                            <Button variant="outlined" className="outlined-button" color="info" startIcon={<IconUpload className="icon-colored" size="18" color={theme.palette.info.main} />} onClick={() => { fileRef.current.click() }}>Upload Document</Button>
                        </Stack>
                    </Stack>}
                    {tabValue === "review-content" && <Stack direction="column" spacing={2} padding="20px" height="100%">
                        <Typography variant="h4" color="var(--main-text-color)">Review Content</Typography>
                        <Stack direction="column" spacing={1}>
                            {/* Map content here */}
                            <Stack direction="column" spacing={1} className="content-status-box">
                                <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                                    <Stack direction="row" spacing={0.5} alignItems="center">
                                        <IconMessage className="icon-colored" size="15px" color="var(--body-text-color-500)" />
                                        <Typography variant="bodyxs" color="var(--body-text-color-500)">Feedback:{feedbackList.length}</Typography>
                                    </Stack>
                                    {/* <Typography variant="bodyregm" color="var(--main-text-color)">Content 1</Typography> */}
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <Typography variant="bodyxs" color="var(--body-text-color-200)">Concept Status:</Typography>
                                        <Chip size="small" sx={CONTENT_STATUS_TO_COLOR[`${approveConcept.approved ? "approved" : "pending"}`]} label={CONTENT_STATUS_TO_LABEL[`${approveConcept.approved ? "approved" : "pending"}`]} />
                                        <Typography variant="bodyxs" color="var(--body-text-color-200)">Content Status:</Typography>
                                        <Chip size="small" sx={CONTENT_STATUS_TO_COLOR[`${approveContent.approved ? "approved" : "pending"}`]} label={CONTENT_STATUS_TO_LABEL[`${approveContent.approved ? "approved" : "pending"}`]} />
                                    </Stack>
                                </Stack>
                                {/* <Stack direction="row" justifyContent="space-between">
                                    <Stack direction="row" spacing={2} alignItems="center">
                                        <Stack direction="row" spacing={0.5} alignItems="center">
                                            <IconMessage className="icon-colored" size="15px" color="var(--body-text-color-500)"/>
                                            <Typography variant="bodyxs" color="var(--body-text-color-500)">Feedback: 2</Typography>
                                        </Stack>
                                        <Stack direction="row" spacing={0.5} alignItems="center">
                                            <IconClock className="icon-colored" size="15px" color="var(--body-text-color-500)"/>
                                            <Typography variant="bodyxs" color="var(--body-text-color-500)">Due: 12/12/2024</Typography>
                                        </Stack>
                                        <Stack direction="row" spacing={0.5} alignItems="center">
                                            <IconEdit className="icon-colored" size="15px" color="var(--body-text-color-500)"/>
                                            <Typography variant="bodyxs" color="var(--body-text-color-500)">Last Updated: 10/12/2024</Typography>
                                        </Stack>
                                    </Stack>
                                </Stack> */}
                                {reviewContentOpen && <Stack direction="column" spacing={2}>
                                    <Divider />
                                    <Stack direction="row" spacing={4}>
                                        <Stack direction="column" spacing={1} maxWidth="50%">
                                            <Typography variant="bodysm" color="var(--main-text-color)">Content Preview</Typography>
                                            <Stack direction="row" spacing={1} alignItems="flex-start">
                                                <Box sx={{ height: "300px", minWidth: "170px", backgroundColor: "var(--neutral-100)", borderRadius: "var(--radius-lg)", border: 1, borderColor: "var(--neutral-200)", overflow: "hidden" }}>
                                                    {deliverable && (deliverable.file || deliverable.link) ? <FileViewer url={deliverable.file || deliverable.link} mimeType={deliverable.fileType} /> : (
                                                        <Stack direction="column" spacing={1} alignItems="center" justifyContent="center" height="100%">
                                                            <Typography variant="bodyxs" color="var(--body-text-color-400)">No content uploaded yet</Typography>
                                                        </Stack>
                                                    )}
                                                </Box>
                                                <Stack direction="column" spacing={1} justifyContent="space-between" height="100%">
                                                    <Stack direction="column" spacing={0.5} sx={{ overflowY: "auto" }}>
                                                        <Typography variant="bodyxs" color="var(--main-text-color)">Caption</Typography>
                                                        <Typography variant="bodyxs" color="var(--body-text-color-500)">
                                                            {deliverable && deliverable.caption ? deliverable.caption : "No caption provided"}
                                                        </Typography>
                                                    </Stack>
                                                    <Button onClick={() => {
                                                        approveConceptContent("Approve-Content", !approveContent.approved)
                                                    }} variant="outlined" className="outlined-button-small" size="small" color="info" startIcon={<IconCheck className="icon-colored" size="18" color={`${approveContent.approved ? "green" : 'var(--body-text-color-500)'}`} />}>{approveContent.approved ? 'Approved' : "Approve"} Content</Button>
                                                </Stack>
                                            </Stack>
                                        </Stack>
                                        <Stack direction="column" spacing={3}>
                                            <Stack direction="column" spacing={1}>
                                                <Typography variant="bodysm" color="var(--main-text-color)">Concept</Typography>
                                                <Typography variant="bodyxs" color="var(--body-text-color-500)" sx={{ whiteSpace: "pre-wrap" }}>{concept || "No concept submitted yet"}</Typography>
                                                <Button onClick={() => {
                                                    approveConceptContent("Approve-Concept", !approveConcept.approved)
                                                }} variant="outlined" className="outlined-button-small" size="small" color="info" startIcon={<IconCheck className="icon-colored" size="18" color={`${approveConcept.approved ? "green" : 'var(--body-text-color-500)'}`} />}>{approveConcept.approved ? 'Approved' : "Approve"} Concept</Button>
                                            </Stack>
                                            <Stack direction="column" spacing={1}>
                                                <Typography variant="bodysm" color="var(--main-text-color)">Feedback</Typography>
                                                <TextField
                                                    value={feedback}
                                                    onChange={(e) => setFeedbak(e.target.value)}
                                                    size="small"
                                                    placeholder="Type your feedback here..."
                                                    multiline
                                                    maxRows={4}
                                                    variant="standard"
                                                    fullWidth
                                                    color="secondary"
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter') {
                                                            e.preventDefault();
                                                            submitFeedback()

                                                        }
                                                    }}
                                                    slotProps={slotProps} />
                                            </Stack>
                                        </Stack>
                                    </Stack>
                                </Stack>}
                                <Stack direction="row">
                                    <Button color="info" startIcon={reviewContentOpen ? <IconChevronUp className="icon-colored" size="18" color="var(--body-text-color-500)" /> : <IconChevronDown className="icon-colored" size="18" color="var(--body-text-color-500)" />}
                                        onClick={() => setReviewContentOpen(!reviewContentOpen)}>{reviewContentOpen ? "Close" : "Review Content"}</Button>
                                </Stack>
                            </Stack>
                        </Stack>
                        <Stack direction="column" spacing={1} sx={{ border: 1, borderColor: theme.palette.divider, borderRadius: "var(--radius-lg)", padding: "16px" }}>
                            <Typography variant="bodyregm" color="var(--main-text-color)">Previous Feedback Notes</Typography>
                            {feedbackList.length === 0 && (
                                <Typography variant="bodysm" color="var(--body-text-color-500)">No feedback submitted yet</Typography>
                            )}
                            {feedbackList.map((data, i) => (
                                <Stack key={i} direction="column" spacing={0.5}>
                                    <Typography variant="bodysm" color="var(--body-text-color-500)">{data.feebback}</Typography>
                                    <Typography variant="bodyxs" color="var(--body-text-color-300)">
                                        {convertDate(data.submittedAt)}
                                    </Typography>
                                    {feedbackList.length !== i + 1 && <Divider />}
                                </Stack>
                            ))}
                        </Stack>
                        {allDeliverables.filter((item) => {
                            const url = item.file || item.link;
                            if (!url) return false;
                            let type = getFileType(url);
                            if (type === 'unknown' && item.fileType) {
                                if (item.fileType.startsWith('image/') || item.fileType.startsWith('video/') || item.fileType.startsWith('application/') || item.fileType.startsWith('text/')) type = 'supported';
                            }
                            return type !== 'unknown';
                        }).length > 0 && (
                            <Stack direction="column" spacing={1} sx={{ border: 1, borderColor: theme.palette.divider, borderRadius: "var(--radius-lg)", padding: "16px" }}>
                                <Typography variant="bodyregm" color="var(--main-text-color)">Previous Content Submissions</Typography>
                                <Stack direction="row" spacing={2} sx={{ overflowX: "auto", paddingBottom: "8px" }}>
                                    {allDeliverables.filter((item) => {
                                        const url = item.file || item.link;
                                        if (!url) return false;
                                        let type = getFileType(url);
                                        if (type === 'unknown' && item.fileType) {
                                            if (item.fileType.startsWith('image/') || item.fileType.startsWith('video/') || item.fileType.startsWith('application/') || item.fileType.startsWith('text/')) type = 'supported';
                                        }
                                        return type !== 'unknown';
                                    }).map((item, i) => (
                                        <Stack key={item.id || i} direction="column" spacing={0.5} sx={{ minWidth: "170px", flexShrink: 0 }}>
                                            <Box sx={{ height: "200px", width: "170px", backgroundColor: "var(--neutral-100)", borderRadius: "var(--radius-lg)", border: 1, borderColor: "var(--neutral-200)", overflow: "hidden" }}>
                                                {(item.file || item.link) ? (
                                                    <FileViewer url={item.file || item.link} mimeType={item.fileType} />
                                                ) : (
                                                    <Stack alignItems="center" justifyContent="center" height="100%">
                                                        <Typography variant="bodyxs" color="var(--body-text-color-400)">No preview</Typography>
                                                    </Stack>
                                                )}
                                            </Box>
                                            <Typography variant="bodyxs" color="var(--main-text-color)">{item.fileName || `Draft ${i + 1}`}</Typography>
                                            <Typography variant="bodyxs" color="var(--body-text-color-300)">{convertDate(transformFirebaseTime(item.submittedAt))}</Typography>
                                            {item.caption && <Typography variant="bodyxs" color="var(--body-text-color-500)" sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "170px" }}>{item.caption}</Typography>}
                                        </Stack>
                                    ))}
                                </Stack>
                            </Stack>
                        )}
                    </Stack>}
                    {tabValue === "affiliate" && <Stack direction="column" spacing={2} padding="20px" height="100%">
                        <Dialog
                            open={trackingModalOpen}
                            fullWidth
                            onClose={handleCloseProductModal}
                        >
                            <DialogTitle>Create a Tracking Link</DialogTitle>
                            <DialogContent>
                                <Typography
                                    variant="subtitle1"
                                    style={{
                                        fontWeight: 800,
                                        textAlign: "left",
                                        marginTop: 20,
                                        marginBottom: 10,
                                    }}
                                >
                                    Your Product URL
                                </Typography>
                                <TextField
                                    fullWidth
                                    onChange={(e) => setProductURL(e.target.value)}
                                ></TextField>
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={handleGenerateURL}>Generate</Button>
                            </DialogActions>
                        </Dialog>
                        <Stack direction="row" justifyContent="space-between">
                            <Typography variant="h4" color="var(--main-text-color)">Affiliate</Typography>
                            <Button onClick={() => setTrackingModalOpen(true)} variant="solid" className="gradient-button" sx={{ color: "white" }} startIcon={<IconPlus className="icon-colored" size="18" color={"white"} />}>Generate Affiliate Link</Button>
                        </Stack>
                        <Stack direction="column" spacing={2}>
                            <TableContainer>
                                <Table sx={{ minWidth: 650 }}>
                                    <TableHead>
                                        <TableRow>
                                        <TableCell>
                                                <Typography variant="bodysm" color="var(--body-text-color-600)">
                                                    Link
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="bodysm" color="var(--body-text-color-600)">
                                                    Conversions
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="bodysm" color="var(--body-text-color-600)">
                                                Full Product URL
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {products.map((product, index) => <TableRow>
                                            <TableCell>
                                                <Typography variant="body2" color="var(--main-text-color)">{`${window.location.origin}/product/${product.id}`}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" color="var(--main-text-color)">{product.visits}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" color="var(--main-text-color)">{product.productURL}</Typography>
                                            </TableCell>
                                        </TableRow>)}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Stack>
                    </Stack>}
                    {tabValue === "post-tracking" && <Stack direction="column" spacing={2} padding="20px" height="100%">
                        <Typography variant="h4" color="var(--main-text-color)">Post Costs</Typography>
                        <Typography variant="bodysm" color="var(--body-text-color-600)">
                            Set the cost and affiliate link for each deliverable. These values are used to calculate CPV, CPE, and other analytics.
                        </Typography>
                        <Stack direction="column" spacing={2}>
                            <TableContainer>
                                <Table sx={{ minWidth: 650 }}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell><Typography variant="bodysm" color="var(--body-text-color-600)">Platform</Typography></TableCell>
                                            <TableCell><Typography variant="bodysm" color="var(--body-text-color-600)">Post Link</Typography></TableCell>
                                            <TableCell sx={{ width: 160 }}><Typography variant="bodysm" color="var(--body-text-color-600)">Price</Typography></TableCell>
                                            <TableCell sx={{ width: 220 }}><Typography variant="bodysm" color="var(--body-text-color-600)">Affiliate Link</Typography></TableCell>
                                            <TableCell sx={{ width: 80 }}></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {liveDeliverables.length === 0 && (
                                            <TableRow>
                                                <TableCell colSpan={5} align="center">
                                                    <Typography variant="bodysm" color="var(--body-text-color-600)">No live deliverables yet. Deliverables will appear here once the creator submits live content.</Typography>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                        {liveDeliverables.map((d) => (
                                            <TableRow key={d.id}>
                                                <TableCell><Typography variant="body2" color="var(--main-text-color)">{d.platform}</Typography></TableCell>
                                                <TableCell>
                                                    {d.link ? (
                                                        <Typography variant="body2" color="var(--main-text-color)" sx={{ maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                            <a href={d.link} target="_blank" rel="noopener noreferrer">{d.link}</a>
                                                        </Typography>
                                                    ) : <Typography variant="body2" color="var(--body-text-color-400)">—</Typography>}
                                                </TableCell>
                                                <TableCell>
                                                    <TextField
                                                        size="small"
                                                        type="number"
                                                        value={editingCosts[d.id]?.totalPrice ?? ""}
                                                        onChange={(e) => setEditingCosts(prev => ({ ...prev, [d.id]: { ...prev[d.id], totalPrice: e.target.value } }))}
                                                        slotProps={{
                                                            input: {
                                                                startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                                            },
                                                        }}
                                                        sx={{ width: 140 }}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <TextField
                                                        size="small"
                                                        placeholder="https://..."
                                                        value={editingCosts[d.id]?.affiliateLink ?? ""}
                                                        onChange={(e) => setEditingCosts(prev => ({ ...prev, [d.id]: { ...prev[d.id], affiliateLink: e.target.value } }))}
                                                        sx={{ width: 200 }}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Button
                                                        size="small"
                                                        variant="contained"
                                                        className="gradient-button"
                                                        sx={{ color: "white", minWidth: 60 }}
                                                        onClick={() => handleSaveCost(d.id)}
                                                    >
                                                        Save
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Stack>
                    </Stack>}
                </Stack>
            </Stack>
        </Stack>
    )
}